import React, { useState, useEffect } from 'react';
import { useApplicationsByVacancyIdStats } from '@/hooks/queries/useApplication';
import Loader from '@/components/Loader';
import { GalleryVerticalEnd, Filter } from 'lucide-react';

const Stats = ({ selectedVacancy, onStatusClick }) => {
    /**
     * API CALL EXPLANATION:
     * 
     * This component makes ONE API call through the useApplicationsByVacancyIdStats hook:
     * 
     * 1. Hook: useApplicationsByVacancyIdStats (from @/hooks/queries/useApplication)
     *    - This hook uses React Query (TanStack Query) to fetch data
     *    - It calls: applicationService.getApplicationsByVacancyIdStats()
     * 
     * 2. Service Method: applicationService.getApplicationsByVacancyIdStats()
     *    - Located in: @/services/applicationService.js
     *    - Makes HTTP GET request to: /vacancy/{vacancyId}/applications/stats
     *    - Query parameters sent: (filters can be passed but typically not needed for stats)
     * 
     * 3. API Endpoint: GET /vacancy/{vacancyId}/applications/stats
     *    - Returns: Response object containing:
     *      - status_summary: Array of status objects with application counts
     *      - Other statistics data
     * 
     * 4. React Query Configuration:
     *    - Query Key: ['applications-stats', selectedVacancy?.id] (if queryKey provided in filters)
     *                  or ['applications', 'vacancy', vacancyId, 'stats'] (default)
     *    - Enabled: Only when selectedVacancy?.id exists
     *    - Cache: Data is not cached (staleTime: 0, cacheTime: 0)
     *    - This means the API is called every time the component mounts or selectedVacancy changes
     */
    const { data: applications, isLoading } = useApplicationsByVacancyIdStats(selectedVacancy?.id, {
        queryKey: ['applications-stats', selectedVacancy?.id]
    });
    const [statusSummary, setStatusSummary] = useState([]);
    const [totalApplications, setTotalApplications] = useState(0);

    useEffect(() => {
        if (applications) {
            const summary = [...(applications.status_summary || [])];
            const vacancyStatuses = selectedVacancy?.vacancy_statuses || [];
            const orderMap = new Map();

            vacancyStatuses.forEach((vs) => {
                const order = parseInt(vs.p_order, 10);
                const pOrder = Number.isNaN(order) ? 999 : order;
                if (vs.id != null) orderMap.set(vs.id, pOrder);
                if (vs.status?.id != null) orderMap.set(vs.status.id, pOrder);
            });

            summary.sort((a, b) => {
                const orderA = orderMap.get(a?.vacancy_status_id) ?? 999;
                const orderB = orderMap.get(b?.vacancy_status_id) ?? 999;
                return orderA - orderB;
            });

            setStatusSummary(summary);
            const total = summary.reduce((acc, status) => acc + status.application_count, 0);
            setTotalApplications(total);
        }
    }, [applications, selectedVacancy]);

    const handleStatusClick = (status) => {
        if (onStatusClick) {
            onStatusClick(status);
        }
    };

    if (!selectedVacancy) {
        return (
            <div className="w-full p-10 text-center">
                <div className="text-muted-foreground mb-4">
                    <GalleryVerticalEnd className="h-14 w-14 mx-auto" />
                </div>
                <p className="text-muted-foreground text-lg font-medium">
                    Please select a vacancy to view applications stats
                </p>
            </div>
        );
    }

    if (isLoading) return <Loader  bgColor="bg-transparent"/>;  

    return (
        <div className="w-full p-4">
            <div className="flex justify-between items-center mb-6 select-none">
                <h2 className="text-xl font-semibold">Applications Stats</h2>
            </div>
            <div className="w-full md:w-1/2 overflow-x-auto bg-card rounded-lg shadow">
                {isLoading ? (
                    <div className="flex justify-center items-center h-64">
                        <Loader  bgColor="bg-transparent"/>
                    </div>
                ) : (
                    <table className="min-w-full divide-y divide-border">
                        <thead className="bg-muted">
                            <tr className="border-b border-border">
                                <th className="px-4 pt-4 pb-2 text-primary-dark font-bold align-top uppercase text-xs tracking-wider">
                                    Status
                                </th>
                                <th className="px-4 pt-4 pb-2 text-primary-dark font-bold align-top uppercase text-xs tracking-wider">
                                    Total Applications ({totalApplications})
                                </th>
                            </tr>
                        </thead>
                        <tbody  className="bg-card divide-y divide-border">
                            {statusSummary?.map((status) => (
                                <tr
                                    key={status?.vacancy_status_id}
                                    onClick={() => handleStatusClick(status)}
                                    className="hover:bg-muted cursor-pointer transition-colors duration-150 group"
                                 
                                >
                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-center  transition-colors">
                                        <div className="flex items-center justify-center gap-2">
                                            {status?.status_name}
                                        </div>
                                    </td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-center  transition-colors">
                                        {status?.application_count} (
                                            {totalApplications > 0
                                                ? ((status?.application_count / totalApplications) * 100).toFixed(1)
                                                : "0.0"
                                            }%
                                        )
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default Stats;


